import { forwardRef } from "react";
import * as RadixMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import cn from "./cn";

// Thin styling over Radix DropdownMenu: arrow-key movement, typeahead,
// Escape, focus return and correct menu roles come from Radix.

export const Menu = RadixMenu.Root;
export const MenuTrigger = RadixMenu.Trigger;
export const MenuGroup = RadixMenu.Group;
export const MenuRadioGroup = RadixMenu.RadioGroup;

export const MenuContent = forwardRef(function MenuContent({ className, align = "end", sideOffset = 6, ...props }, ref) {
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        data-ui="menu"
        className={cn(
          "z-50 min-w-[12rem] rounded-panel border border-default bg-surface-raised p-1 text-foreground shadow-menu focus:outline-none",
          "motion-safe:data-[state=open]:animate-fade-in",
          className
        )}
        {...props}
      />
    </RadixMenu.Portal>
  );
});

export const MenuItem = forwardRef(function MenuItem({ className, tone, icon: Icon, children, asChild = false, ...props }, ref) {
  const itemClassName = cn(
    "flex min-h-8 cursor-default select-none items-center gap-2 rounded-control px-2 text-body-sm outline-none",
    "data-[highlighted]:bg-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50 max-md:min-h-touch",
    tone === "danger" ? "text-danger" : "text-foreground",
    className
  );

  if (asChild) {
    return (
      <RadixMenu.Item ref={ref} asChild className={itemClassName} {...props}>
        {children}
      </RadixMenu.Item>
    );
  }

  return (
    <RadixMenu.Item
      ref={ref}
      className={itemClassName}
      {...props}
    >
      {Icon ? <Icon aria-hidden="true" className={cn("h-4 w-4 shrink-0", tone === "danger" ? "text-danger" : "text-tertiary")} /> : null}
      {children}
    </RadixMenu.Item>
  );
});

export function MenuLabel({ className, ...props }) {
  return <RadixMenu.Label className={cn("px-2 py-1.5 text-meta text-tertiary", className)} {...props} />;
}

export function MenuSeparator({ className }) {
  return <RadixMenu.Separator className={cn("-mx-1 my-1 h-0 border-t border-subtle", className)} />;
}

/** A checked option inside a single-select dropdown menu. */
export const MenuRadioItem = forwardRef(function MenuRadioItem({ className, children, ...props }, ref) {
  return (
    <RadixMenu.RadioItem
      ref={ref}
      className={cn(
        "flex min-h-8 cursor-default select-none items-center gap-2 rounded-control px-2 text-body-sm text-foreground outline-none",
        "data-[highlighted]:bg-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50 max-md:min-h-touch",
        className
      )}
      {...props}
    >
      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-accent">
        <RadixMenu.ItemIndicator>
          <Check aria-hidden="true" className="h-4 w-4" />
        </RadixMenu.ItemIndicator>
      </span>
      {children}
    </RadixMenu.RadioItem>
  );
});
